import React, { useEffect, useRef, useState } from 'react';

import { Grid } from '@mui/material';
import { useFormik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import { AsyncThunkAction } from '@reduxjs/toolkit';

import {
  Agencies,
  getInvoiceById,
  getPharmacistReports,
  getPharmacyReportFilter,
  getPharmacyReports,
  getPharmacyReportsPdf,
  getReportsFilterInfo,
  getReportsPDF,
  ICalendarEventType,
  IPharmacyBookingReports,
  IPharmacyReportParams,
  IReportsData,
  paymentActions,
  paymentInvoicePdf,
  pharmacistActions,
  PharmacistLegendVariant,
  pharmacyActions,
  pharmacyFilterData,
  reportFilterInfo,
  ReportRequestTypes,
  resetStatus,
  ServiceTypes,
  successOrSelector,
  successSelector,
  SupportedTimezones,
  ToUserTypings,
  TypeOfUser,
  userPreferredTimeFormat,
} from '@pharmaplan/common';

import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';

import { useAppSelector } from '../../hooks/useAppSelector';
import useRefresh from '../../hooks/useRefresh';
import useUser from '../../hooks/useUser';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import usePreRequest from '../../hooks/usePreRequest';
import useCancellationBooking from '../../hooks/useCancellationBooking';
import useDrawerNavigation from '../../hooks/useDrawerNavigation';

import {
  availableReportFilter,
  bookingReportFilter,
  paymentReportFilter,
  pharmacyBookingReportFilter,
  pharmacyWorkshiftReportFilter,
  workshiftAndRequestedReportFilter,
} from '../../helpers/Filters';
import IDropdownObject from '../../types/ICustomDropdown';
import { downloadPDF, setPageTitle } from '../../helpers/Functions';
import strings from '../../localization';
import ResponsiveClasses from '../../theme/ResponsiveClasses';
import {
  Constants,
  MomentTimeFormats,
  OutcomeModalTypes,
  ScreenTypes,
} from '../../helpers/Constants';
import { resetDialog, setDialog } from '../../reducers/dialogReducer';
import OutcomeModal from '../Modals/OutcomeModal';

import Drawer from '../Drawer/Drawer';
import DownloadPDF from '../common/DownloadPDF';
import AdsContainer from '../common/AdsContainer';
import DynamicTable from '../DynamicTable';
import CustomButton from '../common/CustomButton/CustomButton';

import reportsMapper, {
  pharmacistHeaders,
  pharmacyHeaders,
  pharmacyReportsMapper,
} from './helpers';
import useGetItems from '../../hooks/useGetItems';
import useLegendReportNavigation from '../../hooks/useLegendReportNavigation';
import useVacationDelete from '../../hooks/useVacationDelete';
import useTableSort from '../../hooks/useTableSort';
import { renderScreen, setOpen } from '../../actions/drawerActions';
import useExpandableFilter from '../../hooks/useExpandableFilter';
import useLongTermProfessionalReportActions from '../../hooks/LongTermContract/useLongTermProfessionalReportActions';
import useLongTermPharmacyActions from '../../hooks/LongTermContract/useLongTermPharmacyActions';
import ViewAllLongTermContracts from '../ViewAllLongTermContracts';
import MultiBookingActionScreen from '../MultiBookingActionScreen';
import { MultiBookingActionType } from '../../hooks/MultiBookingActionScreen/useMultiBookingActions';
import { showSuccess } from '../Admin/Profile/Pharmacist/PharmacistViewProfile/helper';

const serviceType = ServiceHandler.getService();
const isSelfService = serviceType === ServiceTypes.self;

export interface IStataData {
  banners: Array<IDropdownObject>;
  pharmacies: Array<IDropdownObject>;
}

export interface IInitialValues {
  startDate: string;
  endDate: string;
  banners: Array<string>;
  pharmacyId: null | string;
}

export interface IPharmacyInitialValues {
  startDate: string;
  endDate: string;
  pharmacistId: string;
  workshiftTypes: string;
}

const initialValues: IPharmacyInitialValues & IInitialValues = {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  startDate: null,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  endDate: null,
};

export interface IPopoverState {
  open: boolean;
  date: string;
  endDate: string;
  id: string;
  recId: string;
}

const pharmacistInitialValues = {
  banners: [],
  pharmacyId: null,
  source: isSelfService ? Agencies.PharmaGap : Agencies.PharmaPlan,
};

const pharmacyInitialValues = {
  pharmacistId: '',
  workshiftTypes: '',
  source: isSelfService ? Agencies.PharmaGap : Agencies.PharmaPlan,
};
const reportsFileName = 'reports.pdf';
const paymentFileName = 'invoice.pdf';
const watchlistActions = [
  pharmacistActions.deleteWatchlist,
  pharmacistActions.watchlist,
];

const Reports = () => {
  const { state } = useLocation();
  const { showVacationConfirmation } = useVacationDelete();
  const { type, title: headerTitle, start, end } = state ?? {};
  const { manEnd, manStart } = useLegendReportNavigation(type, start, end);
  const { isDesc, orderBy, handleSorting, order } = useTableSort();
  const dispatch = useAppDispatch();
  const [page, setPage] = useState(1);
  const filterValues = useAppSelector(reportFilterInfo);
  const { pdf, reports, userType } = useUser();
  const invoicePdf = useAppSelector(paymentInvoicePdf);
  const pharmacyFilter = useAppSelector(pharmacyFilterData);
  const timeFormat = useAppSelector(userPreferredTimeFormat);
  const loadSuccess = useAppSelector((st) =>
    successOrSelector([pharmacistActions.reports, pharmacyActions.reports], st));
  const requestSuccess = useAppSelector((st) =>
    successOrSelector([pharmacistActions.requestBooking], st));

  const cancelBookingSuccess = useAppSelector((st) =>
    successOrSelector(
      [
        pharmacistActions.cancelMultipleBookings,
        pharmacyActions.cancelMultipleBookings,
      ],
      st,
    ));

  const markAsPaidSuccess = useAppSelector((st) =>
    successSelector([pharmacistActions.markMultiplePaid], st));

  const watchlistSuccess = useAppSelector((st) =>
    successOrSelector(watchlistActions, st));

  const isPharmacy = userType === TypeOfUser.pharmacy;
  const isPharmacist = userType === TypeOfUser.pharmacist;

  const isMounted = useRef(false);

  const [markAsPaidIds, setMarkAsPaidIds] = useState<Array<string>>([]);

  const handleMarkAsPaid = (longTermShiftContractId?: string) =>
    (id: string) => {
      if (longTermShiftContractId) {
        dispatch(
          setDialog({
            Component: (
              <MultiBookingActionScreen
                type={MultiBookingActionType.MarkAsPaid}
                longTermContractId={longTermShiftContractId}
              />
            ),
            heading: {
              title: '',
            },
            maxWidth: 'lg',
            showCloseButton: true,
          }),
        );
        return;
      }
      let idsToSet = [];

      if (markAsPaidIds.includes(id)) {
        idsToSet = markAsPaidIds.filter((item) =>
          item !== id);
      } else {
        idsToSet = [...markAsPaidIds, id];
      }

      setMarkAsPaidIds(idsToSet);
    };

  const { preRequestBooking } = usePreRequest();
  const { requestBooking, handleRequestCancellation } = useLongTermProfessionalReportActions(preRequestBooking);
  const { deleteShift } = useLongTermPharmacyActions();

  const { openDrawer } = useDrawerNavigation();
  const {
    handleSelfCancellation,
    showCancelConfirmation,
    handleCancelConfirmation,
  } = useCancellationBooking();

  const handleCancellation = ({
    id = '',
    isAutoSyncRecord,
    longTermShiftContractId,
    professionalId,
  }: {
    id: string;
    longTermShiftContractId?: string;
    professionalId?: string;
    isAutoSyncRecord?: boolean;
  }) => {
    const isFullService = serviceType === ServiceTypes.full;

    if (longTermShiftContractId) {
      dispatch(
        setDialog({
          Component: (
            <MultiBookingActionScreen
              type={MultiBookingActionType.CancelBooking}
              longTermContractId={longTermShiftContractId}
              professionalId={professionalId}
            />
          ),
          heading: {
            title: '',
          },
          maxWidth: 'lg',
          showCloseButton: true,
        }),
      );
      return;
    }

    if (isSelfService) {
      handleSelfCancellation({ bookingId: id, isAutoSyncRecord })();
      return;
    }

    if (isFullService) {
      showCancelConfirmation({
        bookingId: id,
        onSubmit: handleCancelConfirmation,
      })();
    }
  };

  const userBasedRequest = ToUserTypings({
    [TypeOfUser.pharmacist]: {
      action: preRequestBooking,
    },
    [TypeOfUser.technician]: {
      action: preRequestBooking,
    },
    [TypeOfUser.student]: {
      action: preRequestBooking,
    },
    [TypeOfUser.pharmacy]: {
      action: openDrawer,
    },
  });

  const { action } = userBasedRequest[userType];

  const submitCounterOffer = (bookingId: string, longTermId?: string) => {
    dispatch(setOpen(true));
    dispatch(
      renderScreen({
        screenNumber: longTermId ? 1 : 2,
        screenType: ScreenTypes.serviceWorkshiftDetails,
        eventId: bookingId,
        eventIds: [bookingId],
        type: PharmacistLegendVariant.workshift,
        extraParameters: { isReports: true },
      }),
    );
  };

  const pharmacistConfig = {
    filterRequest: getReportsFilterInfo(type),
    getReport: getPharmacistReports,
    values: pharmacistInitialValues,
    mapper: reportsMapper,
    headers: pharmacistHeaders()[type as keyof typeof pharmacistHeaders],
  };

  const userConfig = ToUserTypings({
    [TypeOfUser.pharmacist]: { ...pharmacistConfig },
    [TypeOfUser.technician]: pharmacistConfig,
    [TypeOfUser.student]: pharmacistConfig,
    [TypeOfUser.pharmacy]: {
      filterRequest: getPharmacyReportFilter(),
      getReport: getPharmacyReports,
      values: pharmacyInitialValues,
      mapper: pharmacyReportsMapper,
      headers: pharmacyHeaders()[type as keyof typeof pharmacyHeaders],
    },
  });

  useEffect(() => {
    setPageTitle(strings.formatString(`${headerTitle}`) as string);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const pdfSuccess = useAppSelector((st) =>
    successOrSelector(
      [pharmacistActions.getReportsPDF, pharmacyActions.reportsPdf],
      st,
    ));

  const invoicePdfSuccess = useAppSelector((st) =>
    successSelector([paymentActions.getInvoiceById], st));
  const [isFiltered, setIsFiltered] = useState(false);

  const formik = useFormik({
    initialValues: { ...initialValues, ...userConfig[userType].values },
    onSubmit: (values) => {
      const { startDate, endDate } = values ?? {};
      const isEmpty = Object.values(values).every(
        (x) =>
          (x as Array<unknown>)?.length === 0 || x === '' || x === null,
      );

      setIsFiltered(!isEmpty);
      dispatch(
        userConfig[userType].getReport({
          type,
          page: 1,
          ...values,
          sortBy: orderBy,
          desc: isDesc,
          startDate: startDate
            ? DateTime.fromISO(startDate).toISO({ includeOffset: false })
            : null,
          endDate: endDate
            ? DateTime.fromISO(endDate).toISO({ includeOffset: false })
            : null,
        }) as AsyncThunkAction<any, IPharmacyReportParams, any>,
      );
    },
  });

  const { filterStates, setShowFilter, showFilter } = useExpandableFilter(
    formik,
    isFiltered,
    type,
  );

  const { refreshReport } = useRefresh({
    type,
    page,
    initialValues: formik.values,
  });

  const handleClear = () => {
    formik.resetForm();
    setPage(1);
    setIsFiltered(false);
    dispatch(
      userConfig[userType].filterRequest as AsyncThunkAction<any, void, any>,
    );
    dispatch(
      userConfig[userType].getReport({
        type,
        page: 1,
        ...{ ...initialValues, ...userConfig[userType].values },
      }) as AsyncThunkAction<any, IPharmacyReportParams, any>,
    );
  };

  const handlePagination = (_: unknown, selectedPage: number) => {
    setPage(selectedPage + 1);
    dispatch(
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      userConfig[userType].getReport({
        type,
        sortBy: orderBy,
        desc: isDesc,
        page: selectedPage + 1,
        ...formik.values,
      }),
    );
  };

  const { getPharmacyName } = useGetItems();

  const bannerName = getPharmacyName(filterValues.banners);

  const reportConfig = () => {
    switch (type) {
      case ReportRequestTypes.booked:
        return isPharmacy
          ? pharmacyBookingReportFilter(pharmacyFilter, handleClear)
          : bookingReportFilter(
            filterValues,
            handleClear,
            bannerName,
            isPharmacist,
          );
      case ReportRequestTypes.watchlist:
      case ReportRequestTypes.workshift:
        return isPharmacy
          ? pharmacyWorkshiftReportFilter(handleClear)
          : workshiftAndRequestedReportFilter(
            filterValues,
            handleClear,
            bannerName,
            isPharmacist,
          );
      case ReportRequestTypes.requested:
        return workshiftAndRequestedReportFilter(
          filterValues,
          handleClear,
          bannerName,
          isPharmacist,
        );
      case ReportRequestTypes.payment:
        return paymentReportFilter(handleClear);
      case ReportRequestTypes.vacation:
      case ReportRequestTypes.availability:
        return availableReportFilter(handleClear);
      default:
        return null;
    }
  };
  const config = reportConfig();

  const handleRender = ({
    id,
    customType,
    isAutoSyncRecord,
  }: {
    id: string;
    customType?: ICalendarEventType;
    isAutoSyncRecord?: boolean;
  }) => {
    openDrawer({
      openDetails: true,
      type: customType ?? type,
      eventIds: [id],
      isAutoSyncIDMap: { [id]: !!isAutoSyncRecord },
    });
  };

  const title = () => {
    const { CANADA } = SupportedTimezones;
    const { month } = MomentTimeFormats;

    const currentMonth = DateTime.now().setZone(CANADA).toFormat(month);

    // ONLY SHOW FOR PHARMACY AND WHEN NOT FILTERED
    if (!isFiltered && isPharmacy) {
      return `[${currentMonth}]`;
    }
    return '';
  };

  const isBooking = type === ReportRequestTypes.booked;

  const handlePDFDownload = () => {
    if (isPharmacy) {
      dispatch(getPharmacyReportsPdf({ type, ...formik.values }));
      return;
    }

    dispatch(getReportsPDF({ reportFilters: { ...formik.values, type } }));
  };

  const navigate = useNavigate();

  const handleInvoice = (
    bookingId: string,
    invoiced: boolean,
    isAutoSyncRecord?: boolean,
  ) => {
    navigate(Constants.paths.generateInvoice, {
      state: { bookingIds: [bookingId], invoiced, isAutoSyncRecord },
    });
  };

  const downloadPaymentReport = (paymentId: string) =>
    () => {
      dispatch(getInvoiceById(paymentId));
    };

  const Table = {
    title: `${headerTitle} ${title()}`,
    headers: userConfig[userType].headers,
    headerBar: [
      {
        Component: (
          <CustomButton
            label={strings.clear}
            variant="outlined"
            onClick={() => {
              handleClear();
            }}
          />
        ),
        key: '3',
        name: 'clear',
        display: !isFiltered,
      },
      {
        key: '1',
        name: 'pdf',
        Component: <DownloadPDF onClick={handlePDFDownload} />,
      },
      {
        icon: filterStates()?.icon,
        key: '2',
        name: 'filter',
        onClick: filterStates()?.onClick,
      },
    ],
    rows:
      userConfig[userType].mapper({
        data: reports?.data as IReportsData[] & IPharmacyBookingReports[],
        type,
        timeFormat,
        dispatch,
        handleRender,
        handleCancellation,
        openDrawer: action,
        requestBooking,
        deleteVacation: showVacationConfirmation,
        handleInvoice,
        handleMarkAsPaid,
        markAsPaidIds,
        submitCounterOffer,
        downloadPaymentReport,
        handleRequestCancellation,
        deleteShift,
      }) ?? [],
  };

  const showRequestSuccess = () => {
    dispatch(
      setDialog({
        showCloseButton: false,
        heading: {
          title: '',
        },
        Component: (
          <OutcomeModal
            type={OutcomeModalTypes.requestSuccess}
            message={strings.requestSuccess}
            showCloseButton
          />
        ),
      }),
    );
  };

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    handleClear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  useEffect(() => {
    if (!(start || end)) {
      handleClear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (pdfSuccess) {
      downloadPDF(pdf.pdfContent, reportsFileName);
      dispatch(
        resetStatus([
          pharmacistActions.getReportsPDF,
          pharmacyActions.reportsPdf,
        ]),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdf.pdfContent, pdfSuccess]);

  useEffect(() => {
    if (invoicePdfSuccess) {
      downloadPDF(invoicePdf.pdfContent, paymentFileName);
      dispatch(resetStatus([paymentActions.getInvoiceById]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoicePdfSuccess]);

  useEffect(() => {
    if (requestSuccess) {
      showRequestSuccess();
      dispatch(resetStatus([pharmacistActions.requestBooking]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestSuccess]);

  useEffect(() => {
    if (cancelBookingSuccess) {
      refreshReport();
      showSuccess(dispatch, strings.bookingCancelledSuccessfully);
      dispatch(
        resetStatus([
          pharmacistActions.cancelMultipleBookings,
          pharmacyActions.cancelMultipleBookings,
        ]),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cancelBookingSuccess]);

  useEffect(() => {
    if (markAsPaidSuccess) {
      refreshReport();
      dispatch(resetDialog());
      dispatch(resetStatus([pharmacistActions.markMultiplePaid]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markAsPaidSuccess]);

  useEffect(() => {
    if (start || end) {
      setShowFilter(true);
      formik.setFieldValue('startDate', manStart);
      formik.setFieldValue('endDate', manEnd);
      formik.handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (watchlistSuccess) {
      dispatch(resetStatus(watchlistActions));
      refreshReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlistSuccess]);

  useEffect(() => {
    if (orderBy) {
      setPage(1);
      dispatch(
        userConfig[userType].getReport({
          type,
          page: 1,
          sortBy: orderBy,
          desc: isDesc,
          ...formik.values,
        }) as AsyncThunkAction<any, IPharmacyReportParams, any>,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, order]);

  useEffect(() => {
    if (isBooking) {
      setMarkAsPaidIds(
        reports?.data
          ?.filter?.((item: IReportsData) =>
            item.markAsPaid)
          ?.map((item: IReportsData) =>
            item.id),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reports.data]);

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} lg={10}>
        <Drawer />
        <DynamicTable
          formik={formik}
          showFilter={showFilter}
          filter={config}
          loadSuccess={loadSuccess}
          page={page}
          totalCount={reports.totalCount}
          count={reports.count}
          handlePagination={handlePagination}
          table={Table}
          order={order}
          orderBy={orderBy}
          handleSort={handleSorting}
        />
      </Grid>
      <Grid sx={ResponsiveClasses.hideOnLg} item xs={2}>
        <AdsContainer />
      </Grid>
    </Grid>
  );
};

export default Reports;
